import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { BlockUI, NgBlockUI }                                 from 'ng-block-ui';
import { TranslateService }                                   from '@ngx-translate/core';

import { CSVService }             from '../core/services/csv.service';
import { ImportOptionsModel }     from './typings/imports.option';
import { ImportsService }         from './imports.service';
import { ImportProposedColumnModel } from './typings/imports.proposed-mapping';
import { ImportRunSummaryModel }   from './typings/imports.summary';
import { ImportStructureModel }   from './typings/imports.import-structure';
import { ImportTheirColumnModel } from './typings/imports.their-column';
import { MessagesService }        from '../messages/messages.service';

@Component({
  selector   : 'wor-import',
  styles: [ require('./import.component.scss') ],
  template: require('./import.component.html')
})
export class ImportComponent implements OnDestroy, OnInit {
  @BlockUI('importBlock') block : NgBlockUI;

  @Input() importOptions: ImportOptionsModel;

  //WS-6786. Whether this import is part of the onboarding wizard. The import hub is, and
  //gets the four step frame from WS-6780. The Countries screen is not: it imports state
  //types through this same component beside a country editing form, and WS-6786 requires
  //that screen to be unaffected. Framed by default so the hub needs no change.
  @Input() framed = true;

  // WS-6801. Told to whoever is showing this import, so the hub above can bring its
  // count and its ticks up to date. The hub's heading is on screen the whole time an
  // import runs, and until now it went on saying nothing had been done while the
  // import underneath reported thousands of records added.
  @Output() finished = new EventEmitter<void>();

  @ViewChild('fileInput') fileInput: ElementRef;

  csvArray               : Array<Array<string>> = [];
  errors                 : Array<number>        = [];
  fileName               : string;
  importStructure        : Array<ImportStructureModel>;
  loading                : boolean;
  ourTransformedColumns  : Array<any>;
  rowHidden              : Array<boolean>;
  selectColumnVisible    : boolean;
  selectedFile           : File;
  theirColumns           : Array<ImportTheirColumnModel>;
  theirTransformedColumns: Array<ImportTheirColumnModel>;

  hasHeaders = true;

  // WS-6784. True only while a file is held over the drop zone, so the zone can show it
  // will accept the drop before the operator lets go of it.
  isDragging = false;

  // WS-6750. The run this business already has going, picked up when the screen opens.
  // Null when there is none, which is the normal case.
  //WS-6780. The file the grid was last built from. Needed because the frame lets the
  //operator go back to step one and forward again, and analyze rebuilds the grid, which
  //throws away every mapping answer they gave. Comparing the file rather than just asking
  //whether a grid exists is what makes choosing a DIFFERENT file still re-read it.
  private analysedFile : File;

  private runInFlightSummary : ImportRunSummaryModel;

  //WS-6780. Which of the four steps is showing. The first two are the operator's to move
  //between. The last two are not: a run that is going, or parked on a question, or
  //finished, decides those, so they are settled below rather than advanced by a button.
  step = 0;

  get runInFlight () : ImportRunSummaryModel {
    return this.runInFlightSummary;
  }

  //Assigned from six places: resuming on open, watching a straight-through run, watching
  //a resumed one, and dropping one. Settling the step here rather than at each of them is
  //what keeps the frame honest, because a step that disagreed with the run would show the
  //operator a mapping table for an import that had already written rows.
  set runInFlight ( summary : ImportRunSummaryModel ) {
    this.runInFlightSummary = summary;

    this.settleStep();
  }

  // WS-6807. True when the run has parked a SECOND time, so the questions now on screen
  // are not the ones the operator has just answered. The screen looks almost identical
  // either way, and without this nothing distinguishes a new question from a button that
  // did nothing.
  isFollowUpQuestion = false;

  // WS-6751. Columns the proposal was not confident about, raised to the operator as
  // questions instead of being guessed at. Empty when there is no proposal, which is
  // every import until WS-6749 ships.
  proposalQuestions : Array<{ index : number, question : string }> = [];

  // True once a proposal has been applied, so the screen can say the mapping was
  // suggested rather than letting the operator assume they built it.
  proposalApplied = false;

  // True only when a proposal was asked for and could not be had. An import that never
  // asks for one is not "unavailable", it is simply manual, and saying otherwise would
  // report a problem that does not exist.
  proposalUnavailable = false;

  // WS-6964. True while the columns are being matched. Holds the screen with a message
  // saying what is happening, which the small spinner it replaced did not.
  proposalRunning = false;

  // WS-6964. True when we asked and got no usable answer, as opposed to getting an
  // answer with nothing in it. These read as the same empty mapping to the operator and
  // they are not the same thing, so they are told apart and worded differently.
  proposalFailed = false;

  // WS-6964. True when the operator chose not to wait. A proposal that lands after this
  // is discarded rather than applied, because by then they may already be mapping by
  // hand and moving their fields underneath them would be worse than no proposal at all.
  proposalSkipped = false;

  constructor (
    protected csvService      : CSVService,
    protected importsService  : ImportsService,
    protected messagesService : MessagesService,
    protected translateService: TranslateService
  ) {}

  addColumn ( columnName : string ) : void {
    const selected = this.theirColumns.find(column => column.name === columnName);

    this.theirTransformedColumns.push({
      name  : selected.name,
      values: [ ...selected.values ]
    });

    this.importStructure.push({
      action    : '',
      ours      : '',
      theirs    : selected.index,
      user_input: []
    });

    this.ourTransformedColumns.push(null);
    this.rowHidden.push(false);

    this.selectColumnVisible = false;
  }

  analyze () : void {
    this.block.start();

    this.loading = true;

    this.csvService.parse(this.selectedFile)
    .then(( csvArray : Array<Array<string>> ) => {
      this.csvArray = csvArray;

      //WS-6780. Set here rather than before the parse so a file that failed to read is tried
      //again the next time the operator moves on, instead of being remembered as analysed and
      //leaving them looking at an empty mapping table.
      this.analysedFile = this.selectedFile;

      //WS-6780. The grid exists, so the columns step now has something to show. analyze() is
      //reached only from the frame moving off step one, so this is the one place that knows
      //the read succeeded.
      this.step = 1;

      this.buildGrid();

      return this.applyProposedMapping();
    })
    .catch(error => {
      this.messagesService.error(error);

      this.fileInput.nativeElement.value = '';
    })
    .finally(() => {
      this.block.stop();

      this.loading = false;
  });
  }

  // WS-6751. Opens the screen with a mapping already proposed, so the operator's job is
  // confirming rather than building. Everything proposed can be overridden, and nothing
  // is imported until they submit, so a proposal is never acted on unconfirmed.
  applyProposedMapping () : Promise<void> {
    this.proposalQuestions   = [];
    this.proposalApplied     = false;
    this.proposalUnavailable = false;
    this.proposalFailed      = false;
    this.proposalSkipped     = false;

    const headings = ( this.theirColumns || [] ).map(column => column.name);

    const rows = ( this.csvArray || [] ).slice(this.hasHeaders ? 1 : 0);

    // WS-6964. Hold the screen while this runs, rather than showing a small spinner that
    // says nothing. It takes noticeably longer on a wide file, which is the file where
    // the suggestions are worth the most, so the wait is longest exactly where giving up
    // costs the operator the most.
    this.proposalRunning = !!this.importOptions.proposalUrl;

    return this.importsService
    .fetchProposedMapping(this.importOptions.proposalUrl, this.importOptions.forTable,
                          headings, rows)
    .then(proposal => {
      // The operator chose to map by hand while this was in flight. Applying a proposal
      // underneath them now would move fields they have already started setting.
      if ( this.proposalSkipped ) {
        return;
      }

      if ( !proposal ) {
        // Only a problem if one was actually asked for.
        this.proposalUnavailable = !!this.importOptions.proposalUrl;

        return;
      }

      // WS-6964. We asked and got no usable answer. Saying "no suggestions" here would
      // tell the operator their file has nothing recognisable in it, which is not what
      // happened and sends them looking at the wrong thing.
      if ( proposal.outcome === 'failed' ) {
        this.proposalFailed = true;

        return;
      }

      if ( !proposal.columns || !proposal.columns.length ) {
        this.proposalUnavailable = !!this.importOptions.proposalUrl;

        return;
      }

      proposal.columns.forEach(column => this.applyProposedColumn(column));

      this.proposalApplied = true;
    })
    .finally(() => {
      this.proposalRunning = false;
    });
  }

  // WS-6964. The operator never has to wait for this. Nothing is lost by skipping:
  // mapping by hand is fully available and always was, and a proposal that arrives after
  // they have skipped is discarded rather than applied underneath them.
  skipProposal () : void {
    this.proposalSkipped = true;
    this.proposalRunning = false;
  }

  applyProposedColumn ( column : ImportProposedColumnModel ) : void {
    const index = Number(column.theirs);

    if ( !this.importStructure || !this.importStructure[ index ] ) {
      return;
    }

    // Not confident, so this is a question rather than a proposal. Left unmapped and
    // put to the operator, because a wrong mapping applied quietly is worse than one
    // more thing to answer.
    if ( !column.confident || !column.ours ) {
      this.proposalQuestions.push({
        index   : index,
        question: column.question || ''
      });

      return;
    }

    this.moveToStructure(index, column.ours);

    // A rule the field implies is deterministic and moveToStructure has already set it.
    // A proposed rule is a suggestion, so it does not overrule one.
    const implied = this.importsService
      .identifierActionFor(this.importOptions.url, column.ours);

    if ( column.action && !implied ) {
      this.importStructure[ index ].action     = column.action;
      this.importStructure[ index ].user_input = [];
    }
  }

  // Questions first, in the order the columns appear in the file.
  // WS-6750. Still going, so the screen says so instead of looking idle.
  get isRunning () : boolean {
    return [ 'queued', 'running' ].includes(this.runInFlight?.status);
  }

  // Waiting on an answer, or done. Both are the summary's job.
  //WS-6780. Step one is done when a file has been read and nothing is still reading it.
  //Not a form control, which is why the step answers canAdvance itself: the wizard's own
  //check looks for invalid controls on the step and a chosen file is not one.
  get canLeaveFileStep () : boolean {
    return !!this.selectedFile && !this.loading;
  }

  //WS-6780. Splits the summary's two jobs across the last two steps. The component is
  //unchanged and renders both, as it always has; only which step shows it differs.
  get isFinished () : boolean {
    return this.runInFlight?.status === 'finished';
  }

  get showsSummary () : boolean {
    return [ 'awaiting_decisions', 'finished' ].includes(this.runInFlight?.status);
  }

  // WS-6807. Nothing has been written by a run that stopped to ask, so leaving it is
  // free. Dropping the run releases the summary and returns the operator to the columns.
  private get needsAnalysis () : boolean {
    return this.selectedFile !== this.analysedFile || !this.csvArray.length;
  }

  //WS-6780. The step the run demands, or the operator's own if it demands none.
  //
  //Deliberately does NOT remember a step across leaving the screen. A run worth returning
  //to is restored by WS-6816 and lands here on the right step by itself. A part-uploaded
  //file cannot be restored at all, because the browser does not hand back the file the
  //operator chose, so remembering step two would return them to an empty mapping table.
  //Starting again is the honest outcome there.
  private settleStep () : void {
    const status = this.runInFlightSummary?.status;

    if ( status === 'finished' ) {
      this.step = 3;

      return;
    }

    if ( [ 'queued', 'running', 'awaiting_decisions' ].includes(status) ) {
      this.step = 2;

      return;
    }

    //No run. The last two steps have nothing to show, so fall back to the columns if we
    //have them and the file if we do not.
    if ( this.step > 1 ) {
      this.step = this.csvArray.length ? 1 : 0;
    }
  }

  //WS-6780. The footer's action for a step is the work that step exists to do, so moving
  //on is what runs it. Reading the file happens on leaving step one, and the import is
  //started on leaving step two. Going back runs nothing.
  onWizardChange ( event : { action : string, currentIndex : number, previousIndex : number } ) : void {
    if ( event.action === 'back' ) {
      //A run that is going, parked or finished owns the step, so the back arrow cannot walk
      //out of it. Abandoning a parked run is the summary's own Back, which drops the run
      //first and then lands here with nothing left to hold the step.
      this.settleStep();

      return;
    }

    if ( event.previousIndex === 0 && event.currentIndex === 1 ) {
      //Nothing new to read, so the move stands and the columns are shown as they were.
      //Re-reading would rebuild the grid and throw away every answer the operator had
      //already given on the columns, which is what going back and forward used to cost.
      if ( !this.needsAnalysis ) {
        return;
      }

      //Hold on the file until it has actually been read. Moving on now would show an empty
      //mapping table, and would keep showing one if the file turned out to be unreadable,
      //which is the blank-screen failure this epic exists to remove. analyze() moves the
      //step on itself once there is a grid to move on to.
      this.step = 0;

      this.analyze();

      return;
    }

    if ( event.previousIndex === 1 && event.currentIndex === 2 ) {
      //Never a second time. The frame can arrive here because a run is already going, and
      //starting another would import the same file twice.
      //starting another would import the same file twice.
      if ( this.runInFlightSummary ) {
        return;
      }

      //Hold on the columns. import() refuses a mapping that still has a column pointing at
      //nothing, and says so against the columns themselves, so the operator has to be looking
      //at them. It also clears the previous run first, which would drag the step back and then
      //forward again a moment later. Staying put and letting the run move the step is both
      //steadier to watch and correct when the import never starts.
      this.step = 1;

      this.import();
    }
  }

  onSummaryBack () : void {
    this.runInFlight = null;

    this.isFollowUpQuestion = false;
  }

  // WS-6807. The answers are saved and the worker is queued to pick the run up again a
  // few seconds later, rather than resumed on the spot. Until it does, the run is still
  // parked, so handing straight to watchRun would read awaiting_decisions, stop at once
  // and leave the operator looking at the questions they have just answered.
  //
  // So wait for the run to actually move, then follow it exactly as a straight-through
  // import is followed. A run that parks a second time comes back here through the
  // summary remounting, which reloads it.
  onDecisionsApplied () : void {
    // Answering clears it. If the run parks again, reportSettledRun sets it back.
    this.isFollowUpQuestion = false;

    // The progress display goes up first and stays up for the whole resume, including
    // the few seconds before the worker claims the run. Leaving it until the run moves
    // was the earlier mistake: the operator answered and got a blank screen telling
    // them it was safe to leave, at the exact moment the design needs them to stay.
    this.importsService.resumeProgress()
    .catch(() => this.importsService.cancelImport());

    this.awaitResume().then(() => {
      this.importsService.watchRun(current => {
        this.runInFlight = current;

        this.importsService.updateStatus(current.status);
      })
      .then(( settled : ImportRunSummaryModel ) => {
        this.importsService.closeProgress();

        this.runInFlight = settled;

        this.reportSettledRun(settled);
      });
    });
  }

  // WS-6807. watchRun resolves on anything outside queued and running, so a run that has
  // parked again to ask a SECOND question is handed back here exactly like an outcome.
  //
  // Every branch has to say something. Before this only finished did, so answering a
  // question and hitting another one closed the progress modal in silence and redrew the
  // questions screen with a different question on it, unmarked. From the operator's seat
  // the button had done nothing. Found on Staging on 11 September, on a twelve row file
  // whose rows each carried two problems.
  protected reportSettledRun ( settled : ImportRunSummaryModel ) : void {
    // The watch gave up rather than the run reaching an outcome. Saying so beats a modal
    // that has simply vanished, and reloading is safe because the run owns the file.
    if ( !settled ) {
      this.messagesService.error(
        this.translateService.instant('IMPORT_SPACE.RUN_STOPPED_RESPONDING'));

      return;
    }

    // Parked again on something the earlier answers did not cover. Nothing has been
    // written and the rows are still held, so this is a question, not a result. The
    // banner is the durable half of saying so; the message autohides.
    if ( settled.status === 'awaiting_decisions' ) {
      this.isFollowUpQuestion = true;

      this.messagesService.show(
        this.translateService.instant('IMPORT_SPACE.RUN_MORE_ANSWERS_NEEDED'), 'warning');

      return;
    }

    if ( settled.status === 'failed' ) {
      this.messagesService.error(
        settled.result_message || this.translateService.instant('IMPORT_SPACE.RUN_FAILED'));

      return;
    }

    // Cancelled is the remaining case and stays silent, because the operator did it and
    // already knows. Anything else at this point is a finished run.
    if ( settled.status !== 'finished' ) {
      return;
    }

    this.messagesService.show(
      settled.result_message || this.translateService.instant('IMPORT_SPACE.IMPORT_FINISHED'),
      'success');

    this.finished.emit();
  }

  // Polls until the run leaves the parked state. Bounded rather than open ended: a
  // worker that never arrives should leave the screen showing the questions, which is
  // the truth, instead of waiting for ever on something that is not coming.
  protected awaitResume ( attempts = 0 ) : Promise<void> {
    if ( attempts >= 15 ) {
      return Promise.resolve();
    }

    return this.importsService.fetchRunSummary()
    .then(summary => {
      this.runInFlight = summary;

      if ( summary && summary.status !== 'awaiting_decisions' ) {
        return;
      }

      return new Promise<void>(resolve => setTimeout(resolve, 2000))
      .then(() => this.awaitResume(attempts + 1));
    });
  }

  // Rows in the operator's file, header excluded. The summary needs it to work out how
  // many will import from how many will not.
  get totalRows () : number {
    if ( !this.csvArray?.length ) {
      return 0;
    }

    return this.hasHeaders ? this.csvArray.length - 1 : this.csvArray.length;
  }

  get orderedQuestions () : Array<{ index : number, question : string }> {
    return [ ...this.proposalQuestions ].sort((a, b) => a.index - b.index);
  }

  isQuestion ( index : number ) : boolean {
    return this.proposalQuestions.some(question => question.index === index);
  }

  // WS-6782. The operator's own values, so a question about a column is answered by
  // looking at it rather than by guessing from its name. Blanks and repeats are
  // dropped, because four copies of the same value tell them nothing.
  exampleValues ( index : number ) : Array<string> {
    const values = this.theirTransformedColumns?.[ index ]?.values || [];

    return values
      .map(value => ( value === null || value === undefined ) ? '' : String(value).trim())
      .filter(value => !!value)
      .filter(( value, position, list ) => list.indexOf(value) === position)
      .slice(0, 4);
  }

  // WS-6781. The mapping is read rather than built.
  //
  // The old grid gave every column two empty dropdowns and the better part of two
  // hundred pixels, so a thirty six column file was a scroll rather than a glance, and
  // the two columns actually needing an answer sat buried among the thirty four that
  // did not. Each column now states what it was matched to and whether it still wants
  // the operator, and only the row they open shows the controls.
  expandedRow : number = null;

  // Columns wanting an answer first, then the rest in file order. Ordering by state
  // rather than by position is the point: two decisions in a file of thirty six should
  // not have to be hunted for.
  get columnOrder () : Array<number> {
    const indexes = ( this.theirTransformedColumns || [] ).map(( column, index ) => index);

    return indexes.sort(( a, b ) => {
      const aFirst = this.needsDecision(a) ? 0 : 1;
      const bFirst = this.needsDecision(b) ? 0 : 1;

      return aFirst === bFirst ? a - b : aFirst - bFirst;
    });
  }

  get columnCount () : number {
    return ( this.theirTransformedColumns || [] ).length;
  }

  get decisionCount () : number {
    return ( this.theirTransformedColumns || [] )
      .filter(( column, index ) => this.needsDecision(index)).length;
  }

  get matchedCount () : number {
    return ( this.theirTransformedColumns || [] )
      .filter(( column, index ) => this.isMatched(index)).length;
  }

  // Deliberately set aside by the operator, which is not the same as unanswered. The
  // existing screen already tracks it this way, so the two agree.
  isIgnored ( index : number ) : boolean {
    return !!this.rowHidden?.[ index ];
  }

  isMatched ( index : number ) : boolean {
    return !!this.importStructure?.[ index ]?.ours;
  }

  needsDecision ( index : number ) : boolean {
    if ( this.isIgnored(index) ) {
      return false;
    }

    return this.isQuestion(index) || !this.isMatched(index);
  }

  // What the column was matched to, in the words the operator sees everywhere else,
  // falling back to the stored name if we hold no display name for it.
  ourFieldLabel ( index : number ) : string {
    const ours = this.importStructure?.[ index ]?.ours;

    if ( !ours ) {
      return null;
    }

    const match = ( this.importOptions.ourColumns || [] )
      .find(column => column.name === ours);

    return match ? match.display : ours;
  }

  // One row open at a time. Opening a second closes the first, so the list cannot grow
  // back into the wall of controls this replaced.
  toggleRow ( index : number ) : void {
    this.expandedRow = this.expandedRow === index ? null : index;
  }

  buildGrid () : void {
    if ( !this.csvArray?.length ) {
      return;
    }

    this.importStructure         = [];
    this.ourTransformedColumns   = [];
    this.rowHidden               = [];
    this.theirColumns            = [];
    this.theirTransformedColumns = [];

    // Now lets loop through the csv array and manipulate each array inside of it
    for ( let i = 0; i < this.csvArray[ 0 ].length ; i++ ) {
      // If first row contains column names
      if ( this.hasHeaders ) {

        // Each column gets an object in our import structure with empty data except
        // 'theirs'. Their column names don't matter on the backend so just specify the
        // index in the row
        this.importStructure.push({
          action    : 'ignore_column',
          ours      : '',
          theirs    : i,
          user_input: []
        });

        // BUT their column needs to display the name so do the same thing as the
        // import structure but display the name instead of the index
        this.theirTransformedColumns.push({
          name  : this.csvArray[ 0 ][ i ],
          values: []
        });

        // Build a list of column names for the new column dropdown
        this.theirColumns.push({
          index : i,
          name  : this.csvArray[ 0 ][ i ],
          values: []
        });

        let x = 1;

        // No we need to loop through the csv array to get some example data.
        // Get the first four records after the column names or all the records,
        // whichever comes first
        while ( x <= 4 && x < this.csvArray.length ) {
          this.theirTransformedColumns[ i ].values.push(this.csvArray[ x ][ i ]);

          this.theirColumns[ i ].values.push(this.csvArray[ x ][ i ]);

          x++;
        }
      }
      // If first row doesn't contain column names
      else {
        // Same as above, except we create a column row, with fake names to display
        this.importStructure.push({
          theirs    : i,
          ours      : '',
          action    : 'ignore_column',
          user_input: []
        });

        this.theirTransformedColumns.push({
          name  : `Column${ i + 1 }`,
          values: []
        });

        this.theirColumns.push({
          index : i,
          name  : `Column${ i + 1 }`,
          values: []
        });

        let x = 0;

        // This is the same too except we can start at 0 since their are no
        // column names to worry about
        while ( x < 4 && x < this.csvArray.length ) {
          this.theirTransformedColumns[ i ].values.push(this.csvArray[ x ][ i ]);

          this.theirColumns[ i ].values.push(this.csvArray[ x ][ i ]);

          x++;
        }
      }

      this.rowHidden.push(false);
    }
  }

  chooseColumn () : void {
    this.selectColumnVisible = true;
  }

  ignore ( index : number ) : void {
    this.importStructure[ index ].action = 'ignore_column';
    this.rowHidden[ index ]              = true;

    // WS-6782. Setting a column aside IS an answer to its question, so the question
    // goes with it. Choosing a field already drops the question, in moveToStructure,
    // but the ignore route did not, so a question the operator had just dealt with
    // stayed on screen and the count above the list kept counting it.
    this.proposalQuestions = this.proposalQuestions
      .filter(question => question.index !== index);
  }

  // Every column starts as ignore_column, and the guard below only ever fired on a
  // column that was mapped to nothing. So an import in which the operator mapped
  // nothing at all passed straight through: the backend accepted it, ran it, wrote no
  // rows, and reported success. WS-6753 then ticked the type complete, because it asks
  // whether an import record exists rather than whether anything was imported.
  //
  // That is a worse failure than the one this epic exists to remove. A visible failure
  // prompts a retry. A success that imports nothing means a trial customer believes
  // their data is loaded and moves on.
  get hasMappedColumn () : boolean {
    return ( this.importStructure || [] )
      .some(row => row.action !== 'ignore_column' && !!row.ours);
  }

  import () : void {
    this.errors = [];

    for ( let i = 0; i < this.importStructure.length; i++ ) {
      const row = this.importStructure[ i ];

      if ( row.action && row.action !== 'ignore_column' && !row.ours ) {
        this.errors.push(i);
      }
    }

    if ( this.errors.length ) {
      this.messagesService.show(this.translateService.instant('JS_SPACE.IMPORTS.SELECT_OR_SPECIFY_COLUMNS'), 'error');

      return;
    }

    if ( !this.hasMappedColumn ) {
      this.messagesService.show(this.translateService.instant('IMPORT_SPACE.NOTHING_MAPPED'), 'error');

      return;
    }

    // WS-6807. Drop the previous run before starting this one.
    //
    // ngOnInit restores a finished run so the operator can come back to rows nobody has
    // dealt with, which is WS-6816 and is right. But it leaves runInFlight holding that
    // run, and showsSummary reads it, so submitting a new import kept the PREVIOUS
    // import's result on screen: the operator was looking at the download-the-rows
    // screen from the file before while a different file was being imported behind it.
    // Nothing tells them, because the screen is showing a real result, just not theirs.
    this.runInFlight = null;

    // A new file's questions are never a follow up to the last file's.
    this.isFollowUpQuestion = false;

    this.importsService.start(this.importOptions.url, this.selectedFile, this.importStructure, this.hasHeaders)
    // WS-6789. start() resolves with the run summary, not a string. Handing that object
    // to the message service put it through a text pipe, which threw, which rejected the
    // promise, which showed a RED ERROR on an import that had just succeeded. Precisely
    // the false-failure signal the epic was built to remove, and it makes the operator
    // upload again and write every customer twice.
    .then(( summary : ImportRunSummaryModel ) => {
      this.runInFlight = summary;

      // WS-6807. A run that stopped to ask something has NOT finished, and must not be
      // reported as though it had.
      //
      // start() resolves as soon as the run settles, and parking to ask a question is
      // settling. A parked run carries no result_message, so this fell through to
      // "Import finished" in green on a run that had written nothing and was waiting on
      // an answer about rows it could not take. showsSummary puts the questions up, so
      // the operator got a success message and a screen full of questions at once.
      //
      // The same false-signal defect WS-6789 fixed, in the opposite direction: that one
      // reported failure on success, this one reports success on unfinished.
      if ( summary?.status === 'awaiting_decisions' ) {
        return;
      }

      const message = summary?.result_message
        || this.translateService.instant('IMPORT_SPACE.IMPORT_FINISHED');

      this.messagesService.show(message, 'success');

      // The result stays on screen. This only tells the hub to recount, so the
      // heading agrees with what the operator has just been told.
      this.finished.emit();
    })
    .catch(error => this.messagesService.error(error));
  }

  moveToStructure ( index : number, columnName : string ) : void {
    const selected = this.importOptions.ourColumns.find(column => column.name === columnName);

    this.ourTransformedColumns[ index ] = selected;

    this.errors = this.errors.filter(error => error !== index);

    if ( !selected ) {
      return;
    }

    this.importStructure[ index ].ours = selected.name;

    // WS-6751. Where the field determines the correction rule, set it. Choosing
    // Imported ID already says the column is the customer's own identifier, and making
    // the operator then pick "Is Customer ID" is the screen asking them to state the
    // same fact twice. Raised by a customer in September 2025 against customer data and
    // again against vehicle data.
    const implied = this.importsService
      .identifierActionFor(this.importOptions.url, selected.name);

    if ( implied ) {
      this.importStructure[ index ].action     = implied;
      this.importStructure[ index ].user_input = [];

      // The column is answered now, so it stops being a question.
      this.proposalQuestions = this.proposalQuestions
        .filter(question => question.index !== index);

      return;
    }

    this.proposalQuestions = this.proposalQuestions
      .filter(question => question.index !== index);

    if ( this.importStructure[ index ].action === 'ignore_column' ) {
      if ( this.importsService.isPhoneColumn(selected.name) ) {
        this.importStructure[ index ].action = 'extract_numeric';
      }
      else {
        this.importStructure[ index ].action = 'leave_as_is';
      }
    }
  }

  ngOnDestroy () : void {
    this.fileInput.nativeElement.value = '';

    this.importsService.stopWatchingRun();
  }

  // WS-6750. An import outlives the screen that started it, so opening this screen has
  // to ask whether one is already going rather than assume not. Without this, leaving
  // and coming back shows an idle screen next to a running import, and the operator
  // starts a second one.
  ngOnInit () : void {
    // WS-6816. Asks for a run worth returning to, not only one still going. A finished
    // run with rows nobody has dealt with is picked up here, so leaving and coming back
    // lands on the result and its outstanding rows rather than a blank screen.
    //
    // watchRun is still called for it. That is deliberate and costs one request: it
    // resolves immediately for anything that is not queued or running, so a finished run
    // simply settles where it already is rather than needing a separate path.
    this.importsService.runToResume().then(summary => {
      if ( !summary ) {
        return;
      }

      this.runInFlight = summary;

      this.importsService.watchRun(current => {
        this.runInFlight = current;
      })
      .then(finished => {
        this.runInFlight = finished;
      });
    });
  }

  onActionChange ( index : number ) : void {
    this.importStructure[ index ].user_input = [];
  }

  onFileSelect ( event : Event ) : void {
    const input = event.target as HTMLInputElement;

    if ( input.files && input.files.length > 0 ) {
      this.useFile(input.files[ 0 ]);

      if ( !this.selectedFile ) {
        input.value = '';
      }
    }
  }

  // WS-6784. Dropping a file and choosing one are the same act, so both end in
  // useFile rather than each keeping its own idea of what has been picked.
  onDragLeave () : void {
    this.isDragging = false;
  }

  onDragOver ( event : DragEvent ) : void {
    // Both of these are needed. Without them the browser treats the drop as a
    // navigation, leaves the app and opens the file, losing anything already set up.
    event.preventDefault();
    event.stopPropagation();

    this.isDragging = true;
  }

  onDrop ( event : DragEvent ) : void {
    event.preventDefault();
    event.stopPropagation();

    this.isDragging = false;

    const file = event.dataTransfer?.files?.[ 0 ];

    if ( file ) {
      this.useFile(file);
    }
  }

  useFile ( file : File ) : void {
    if ( file ) {
      this.selectedFile = file;
      this.fileName     = this.selectedFile.name;
    }
    else {
      this.selectedFile = null;
      this.fileName     = null;
    }
  }

  selectedEquation ( index : number ) : any {
    const action = this.importStructure[ index ]?.action;

    if ( !action ) {
      return null;
    }

    return this.importOptions.transformFunctions.find(f => f.action === action) || null;
  }

  toggleBox () : void {
    this.importOptions.collapsed = !this.importOptions.collapsed;
  }
}
